import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { AIStylesContainer } from "./Styles/AIStylesContainer"
import { pushDialog, replaceDialog, resetDialogs } from "@/store/dialogs-state"
import { CreateStyleDialog, type CreateStyleSelectOptions } from "./Dialogs/CreateStyleDialog"
import { showSelectFolderDialog } from "@/store/project/project"
import { StyleDraft } from "@/store/style/style-state"
import { StyleResponseDTO } from "@/api/dto/styles"
import { Button } from "./ui/Button"
import { useStyleRouteNavigation, useStyleRouteScene } from "@/routing/route-state"
import { StyleTab } from "@/store/style/style-types"
import { CollectionResponseDTO } from "@/api/PSApiOperations"
import { PixiesetService } from "@/store/Pixieset/PixiesetService"
import { ConfirmDialog } from "./Dialogs/ConfirmDialog"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { routes } from "@/utils/routes"
import { useRouterState } from "@tanstack/react-router"
import { isDuplicateStyleNameError, StyleService } from "@/store/style/style-service"
import { publishStyleDraft } from "@/store/style/publish-style"
import { popActiveLoadingDialogs } from "@/store/loading-dialog-utils"

const hasCollectionIdInSearchObject = (search: unknown): search is { collection_id?: unknown } =>
	search !== null &&
	typeof search === "object" &&
	Object.prototype.hasOwnProperty.call(search, "collection_id")

const normalizeCollectionId = (value: unknown): string | null => {
	const candidate: unknown = Array.isArray(value) ? value[0] : value
	if (typeof candidate === "string") {
		const trimmed = candidate.trim()
		return trimmed.length > 0 ? trimmed : null
	}
	if (typeof candidate === "number" && Number.isFinite(candidate)) {
		return String(candidate)
	}
	return null
}

const parseCollectionIdFromSearch = (
	search: unknown,
): {
	hasCollectionParam: boolean
	normalizedId: string | null
} => {
	if (typeof search === "string") {
		const params = new URLSearchParams(search)
		return {
			hasCollectionParam: params.has("collection_id"),
			normalizedId: normalizeCollectionId(params.getAll("collection_id")),
		}
	}

	if (search instanceof URLSearchParams) {
		return {
			hasCollectionParam: search.has("collection_id"),
			normalizedId: normalizeCollectionId(search.getAll("collection_id")),
		}
	}

	if (!hasCollectionIdInSearchObject(search)) {
		return {
			hasCollectionParam: false,
			normalizedId: null,
		}
	}

	return {
		hasCollectionParam: true,
		normalizedId: normalizeCollectionId(search.collection_id),
	}
}

const getStyleTabForStyle = (style: StyleResponseDTO): StyleTab => {
	if (style.visibility === "internal") {
		return "internal"
	}
	if (style.visibility === "public") {
		return "polarr"
	}
	return "user"
}

export const AIStyles = () => {
	const { t } = useTranslation("components")
	const scene = useStyleRouteScene()
	const { toList, toDetail } = useStyleRouteNavigation()
	const navigateToStyleCreate = routes.styleCreate.useNavigate()
	const search = useRouterState({ select: (state) => state.location.search })
	const [detailRouteStyle, setDetailRouteStyle] = useState<{
		style: StyleResponseDTO
		source: StyleTab
	} | null>(null)
	const [isLoadingDetailStyle, setIsLoadingDetailStyle] = useState(false)
	const [hasDetailStyleError, setHasDetailStyleError] = useState(false)
	const handledCreateRouteRef = useRef<string | null>(null)
	const collectionSearch = useMemo(() => parseCollectionIdFromSearch(search), [search])
	const hasCollectionParam = collectionSearch.hasCollectionParam
	const normalizedCollectionId = collectionSearch.normalizedId

	const handleUseStyle = (selectedStyleDTO: StyleResponseDTO) => {
		showSelectFolderDialog({ selectedStyleDTO })
	}

	const getSearchWithoutCollectionId = useCallback(() => {
		if (search instanceof URLSearchParams) {
			const params = new URLSearchParams(search)
			params.delete("collection_id")
			return Object.fromEntries(params.entries())
		}
		if (typeof search === "string") {
			const params = new URLSearchParams(search)
			params.delete("collection_id")
			return Object.fromEntries(params.entries())
		}
		if (hasCollectionIdInSearchObject(search)) {
			const nextSearch = { ...search }
			delete nextSearch.collection_id
			return nextSearch
		}
		return search
	}, [search])

	const clearStyleCreateRoute = useCallback(() => {
		if (scene.pathname === routes.styleCreate.path) {
			void toList({ replace: true, search: getSearchWithoutCollectionId() })
		}
	}, [getSearchWithoutCollectionId, scene.pathname, toList])

	const publishDraftFromStyleCreate = useCallback(
		async (options: Extract<CreateStyleSelectOptions, { styleDraft: StyleDraft }>) => {
			try {
				const publishResult = await publishStyleDraft(options.styleDraft)

				let createdStyle: StyleResponseDTO | undefined
				try {
					createdStyle = await StyleService.fetchStyle(publishResult.styleInfo.styleId)
				} catch (fetchError) {
					console.warn(
						"Style created, but failed to fetch created style details for project apply.", // i18n-ignore
						fetchError,
					)
				}

				resetDialogs()
				replaceDialog(ConfirmDialog, {
					title: t("aiStyles.styleCreated"),
					message: t("aiStyles.styleCreatedMessage", {
						name: publishResult.styleInfo.name,
					}),
					trackingViewName: "generate_style_success_dialog",
					secondary: {
						title: t("aiStyles.notNow"),
						handler: () => {
							clearStyleCreateRoute()
						},
					},
					primary: {
						title: t("aiStyles.useStyle"),
						handler: () => {
							clearStyleCreateRoute()
							showSelectFolderDialog(
								createdStyle ? { selectedStyleDTO: createdStyle } : undefined,
							)
						},
					},
				})
			} catch (error) {
				if (isDuplicateStyleNameError(error)) {
					throw error
				}

				console.error("Error creating style from images/gallery:", error)
				popActiveLoadingDialogs()
				pushDialog(ConfirmDialog, {
					title: t("aiStyles.failedCreateTitle"),
					message: t("aiStyles.failedCreateMessage"),
					primary: {
						title: t("common:action.confirm"),
						handler: () => {
							clearStyleCreateRoute()
						},
					},
					trackingViewName: "generate_style_failure_dialog",
				})
			}
		},
		[clearStyleCreateRoute],
	)

	const handleCreateStyleSelected = useCallback(
		(options: Extract<CreateStyleSelectOptions, { styleDraft: StyleDraft }>) => {
			return publishDraftFromStyleCreate(options)
		},
		[publishDraftFromStyleCreate],
	)

	const openCreateStyleDialog = useCallback(
		(initialCollection?: CollectionResponseDTO) => {
			replaceDialog(CreateStyleDialog, {
				trackingViewName: "create_style_dialog",
				initialCollection,
				onSelect: (options) => {
					// i18n-ignore: "From Manual Edit" is hidden in AIStyles flow, so we only expect draft output here.
					if (!("styleDraft" in options)) {
						console.warn(
							"[AIStyles] Unexpected non-draft style create selection",
							options,
						)
						return
					}
					return handleCreateStyleSelected(options)
				},
				onAllDialogsClosed: clearStyleCreateRoute,
			})
		},
		[clearStyleCreateRoute, handleCreateStyleSelected],
	)

	const createNewStyle = () => {
		if (scene.pathname === routes.styleCreate.path) {
			openCreateStyleDialog()
			return
		}
		void navigateToStyleCreate({ search: getSearchWithoutCollectionId() })
	}

	const styleIdFromPath = scene.styleId
	const isDetailRoute = scene.isStyleDetailRoute

	const showCollectionPermissionError = useCallback(() => {
		replaceDialog(ConfirmDialog, {
			trackingViewName: "create_style_invalid_collection",
			title: t("aiStyles.unableCreateTitle"),
			message: t("aiStyles.unableCreateMessage"),
			primary: { title: t("common:action.confirm") },
		})
	}, [])

	const cleanUpCollectionIdFromSearch = useCallback(() => {
		if (!collectionSearch.hasCollectionParam) {
			return
		}
		void navigateToStyleCreate({ replace: true, search: getSearchWithoutCollectionId() })
	}, [collectionSearch.hasCollectionParam, getSearchWithoutCollectionId, navigateToStyleCreate])

	useEffect(() => {
		if (scene.pathname !== routes.styleCreate.path) {
			handledCreateRouteRef.current = null
			return
		}

		const routeKey = hasCollectionParam
			? (normalizedCollectionId ?? "__invalid_collection_id__")
			: "__no_collection_id__"
		if (handledCreateRouteRef.current === routeKey) {
			return
		}
		handledCreateRouteRef.current = routeKey

		if (hasCollectionParam && !normalizedCollectionId) {
			showCollectionPermissionError()
			return
		}

		if (!normalizedCollectionId) {
			openCreateStyleDialog()
			return
		}
		if (!isPixiesetAuthEnabled()) {
			showCollectionPermissionError()
			return
		}

		void PixiesetService.getCollection(normalizedCollectionId)
			.then((selectedCollection) => {
				if (window.location.pathname !== routes.styleCreate.path) {
					return
				}
				if (!selectedCollection) {
					showCollectionPermissionError()
					return
				}
				// Mark no-param route as already handled before URL cleanup to avoid opening
				// a second create dialog without preselected collection on the cleanup rerender.
				handledCreateRouteRef.current = "__no_collection_id__"
				cleanUpCollectionIdFromSearch()
				openCreateStyleDialog(selectedCollection)
			})
			.catch((error) => {
				if (window.location.pathname !== routes.styleCreate.path) {
					return
				}
				console.error("Failed to validate collection for style create link", error)
				showCollectionPermissionError()
			})
	}, [
		cleanUpCollectionIdFromSearch,
		hasCollectionParam,
		normalizedCollectionId,
		openCreateStyleDialog,
		scene.pathname,
		showCollectionPermissionError,
	])

	const styleNotFoundMessage = t("aiStyles.styleNotFoundMessage")

	const detailStyle = detailRouteStyle?.style ?? null
	const detailStyleSource = detailRouteStyle?.source

	useEffect(() => {
		if (!isDetailRoute) {
			setDetailRouteStyle(null)
			setIsLoadingDetailStyle(false)
			setHasDetailStyleError(false)
			return
		}

		if (!styleIdFromPath || detailRouteStyle?.style.styleId === styleIdFromPath) {
			return
		}

		let isCancelled = false
		setIsLoadingDetailStyle(true)
		setHasDetailStyleError(false)

		void StyleService.fetchStyle(styleIdFromPath)
			.then((style) => {
				if (isCancelled) {
					return
				}
				setDetailRouteStyle({
					style,
					source: getStyleTabForStyle(style),
				})
			})
			.catch((error) => {
				if (isCancelled) {
					return
				}
				console.warn("Failed to fetch style detail route.", error)
				setDetailRouteStyle(null)
				setHasDetailStyleError(true)
			})
			.finally(() => {
				if (!isCancelled) {
					setIsLoadingDetailStyle(false)
				}
			})

		return () => {
			isCancelled = true
		}
	}, [detailRouteStyle?.style.styleId, isDetailRoute, styleIdFromPath])

	const shouldShowLoading =
		isDetailRoute && !detailStyle && (isLoadingDetailStyle || !hasDetailStyleError)
	const shouldShowNotFound =
		isDetailRoute && !detailStyle && !isLoadingDetailStyle && hasDetailStyleError

	const handleDetailViewChange = useCallback(
		(isDetailView: boolean, style?: StyleResponseDTO, tab?: StyleTab) => {
			if (isDetailView && style) {
				setDetailRouteStyle({
					style,
					source: tab ?? getStyleTabForStyle(style),
				})
				setHasDetailStyleError(false)
				if (styleIdFromPath === style.styleId) {
					return
				}
				void toDetail(style.styleId)
				return
			}

			if (!isDetailView && styleIdFromPath) {
				setDetailRouteStyle(null)
				void toList()
			}
		},
		[styleIdFromPath, toDetail, toList],
	)

	const renderFallback = () => {
		if (shouldShowLoading) {
			return (
				<div className="flex flex-1 items-center justify-center text-sm text-base-400">
					<div className="flex flex-col items-center space-y-2">
						<div className="size-8 animate-spin rounded-full border-2 border-accent-600 border-t-transparent" />
						<span>{t("aiStyles.loadingDetails")}</span>
					</div>
				</div>
			)
		}

		if (shouldShowNotFound) {
			return (
				<div className="flex flex-1 flex-col items-center justify-center space-y-4 text-center">
					<div className="text-lg font-semibold text-base-100">
						{t("aiStyles.styleNotFound")}
					</div>
					<div className="max-w-md text-sm text-base-400">{styleNotFoundMessage}</div>
					<Button
						trackingLabel="style_detail_back"
						aria-label={t("aiStyles.ariaBackToAIStylesList")}
						onClick={() => void toList()}
					>
						{t("aiStyles.backToStyles")}
					</Button>
				</div>
			)
		}

		return null
	}

	const detailStyleProp = isDetailRoute && detailStyle ? detailStyle : undefined
	const detailSourceProp = isDetailRoute ? detailStyleSource : undefined

	return (
		<div className="flex size-full flex-col overflow-hidden">
			{(shouldShowLoading || shouldShowNotFound) && renderFallback()}
			{!shouldShowLoading && !shouldShowNotFound && (
				<Suspense>
					<AIStylesContainer
						key={isDetailRoute ? "detail" : "list"}
						title={t("aiStyles.panelTitle")}
						onAddNew={createNewStyle}
						showTitle={true}
						isFullWidth={true}
						onUse={handleUseStyle}
						onDetailViewChange={handleDetailViewChange}
						selectedDetailStyle={detailStyleProp}
						selectedDetailSource={detailSourceProp}
					/>
				</Suspense>
			)}
		</div>
	)
}
export default AIStyles
